import { useId, useState } from 'react';
import _ from 'lodash';

import {
    HexColor,
    hexToUnityColor,
    UnityColor,
    unityColorToHex,
} from '@lifeserver/shared';

import styles from './ColorInput.module.scss';

function isHexColor(color: string): color is HexColor {
    return /^#([0-9A-Fa-f]{6})$/.test(color);
}

export interface ColorInputProps {
    value?: UnityColor;
    onChange?: (value: UnityColor) => void;
    allowedColors: UnityColor[];
    layout?: 'row' | 'grid';
    gridColumns?: number;
    allowCustomColor?: boolean;
}
export const ColorInput = ({
    value,
    onChange,
    allowedColors,
    layout = 'row',
    gridColumns = 3,
    allowCustomColor = false,
}: ColorInputProps) => {
    const id = useId();

    const [customColors, setCustomColors] = useState<UnityColor[]>([]);

    const containerClass =
        layout === 'grid'
            ? `${styles.container} ${styles.gridLayout}`
            : `${styles.container} ${styles.rowLayout}`;

    const gridStyle =
        layout === 'grid'
            ? { gridTemplateColumns: `repeat(${gridColumns}, 50px)` }
            : {};

    return (
        <div className={containerClass} style={gridStyle}>
            {allowedColors.map((color) => (
                <ColorCircleComponent
                    key={unityColorToHex(color)}
                    color={color}
                    id={id}
                    selectedColor={value}
                    onChange={onChange}
                />
            ))}
            {customColors.map((color, index) => (
                <ColorCircleComponent
                    key={`custom-${index}`}
                    color={color}
                    id={id}
                    selectedColor={value}
                    onChange={onChange}
                    custom
                    onColorEdit={(newColor) => {
                        const newCustomColors = [...customColors];
                        newCustomColors[index] = newColor;
                        setCustomColors(newCustomColors);
                        // If this was the selected color, update the selection
                        if (_.isEqual(color, value)) {
                            onChange?.(newColor);
                        }
                    }}
                />
            ))}
            {allowCustomColor && (
                <button
                    type="button"
                    onClick={() => {
                        const newColor: UnityColor = {
                            r: Math.random(),
                            g: Math.random(),
                            b: Math.random(),
                            a: 1,
                        };
                        setCustomColors([...customColors, newColor]);
                    }}
                    className={styles.addColorButton}
                    title="Add new color"
                >
                    <span className={styles.plusIcon}>+</span>
                </button>
            )}
        </div>
    );
};

export const ColorCircleComponent = ({
    color,
    id,
    selectedColor,
    onChange,
    custom = false,
    onColorEdit,
    hideEditButton = false,
}: {
    color: UnityColor;
    id: string;
    custom?: boolean;
    selectedColor?: UnityColor;
    onChange?: (color: UnityColor) => void;
    onColorEdit?: (newColor: UnityColor) => void;
    hideEditButton?: boolean;
}) => {
    const hexColor = unityColorToHex(color, true);
    const hexColorNoAlpha = unityColorToHex(color, false);
    const isSelected = _.isEqual(color, selectedColor);
    const displayColor = hexColor;

    const handleLabelClick = (e: React.MouseEvent) => {
        if (hideEditButton && custom) {
            e.preventDefault();
            // Find and trigger the color picker
            const colorInput = e.currentTarget.parentElement?.querySelector(
                'input[type="color"]',
            ) as HTMLInputElement;
            colorInput?.click();
        }
    };

    return (
        <div className={styles.colorItem}>
            <input
                id={`${id}${hexColor}`}
                type="radio"
                name={id}
                checked={isSelected}
                onChange={() => {
                    onChange?.(color);
                }}
                className={styles.colorRadio}
            />
            <label
                htmlFor={`${id}${hexColor}`}
                className={`${styles.colorLabel} ${hideEditButton && custom ? styles.clickableCircle : ''}`}
                style={{ background: displayColor }}
                onClick={handleLabelClick}
            />
            {custom && (
                <div className={styles.customButtons}>
                    <input
                        type="color"
                        value={hexColorNoAlpha}
                        onChange={(e) => {
                            if (!isHexColor(e.target.value)) return;
                            const newUnityColor = hexToUnityColor(
                                e.target.value,
                            );
                            // Preserve the existing alpha value
                            onColorEdit?.({ ...newUnityColor, a: color.a });
                        }}
                        className={styles.colorPicker}
                        style={{
                            opacity: 0,
                            pointerEvents: hideEditButton ? 'none' : 'auto',
                        }}
                    />
                    {!hideEditButton && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // Trigger the color input click
                                const colorInput = e.currentTarget
                                    .previousElementSibling as HTMLInputElement;
                                colorInput?.click();
                            }}
                            className={styles.editIcon}
                            title="Edit color"
                        >
                            ✏️
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};
